import { isAxiosError } from "helpers/utils.helper"
import {
	getFurnitureActionLabel,
	isCountertop,
	isCustomFurniture,
	isOtherFurniture,
} from "./FurnitureModelLabel/furnitureModel.helpers"
import type { TEcall, TEref, TErefStatus, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"

// Pulls a single human-readable message out of a DRF-style 4xx error body, for INLINE display
// (e.g. the "Set page name" modal, which keeps itself open on failure so the user can correct the
// value). Handles a bare string, a string array, and the common `{ field: "msg" }` /
// `{ field: ["msg"] }` object shapes (e.g. `{ version: "Version mismatch..." }`,
// `{ name: ["..."] }`, `{ detail: "..." }`). Falls back to a generic message.
export const extractApiErrorMessage = (
	e: unknown,
	fallback = "Something went wrong. Please try again later.",
): string => {
	if (isAxiosError(e)) {
		const data = e.response?.data
		if (typeof data === "string" && data.trim()) return data
		if (Array.isArray(data)) {
			const first = data.find((item) => typeof item === "string" && item.trim())
			if (first) return first as string
		} else if (data && typeof data === "object") {
			for (const value of Object.values(data as Record<string, unknown>)) {
				if (typeof value === "string" && value.trim()) return value
				if (Array.isArray(value)) {
					const first = value.find((item) => typeof item === "string" && item.trim())
					if (first) return first as string
				}
			}
		}
	}
	return fallback
}

// Whether an error is the optimistic-lock version conflict from the page PATCH (DRF returns it with
// a `version` key in the 4xx body). Only this case needs a refetch to re-seed the stale version
// before a retry — name-validation errors leave the version valid, so they must NOT trigger the
// heavy GET quotation refetch.
export const isVersionConflictError = (e: unknown): boolean => {
	if (!isAxiosError(e)) return false
	const data = e.response?.data
	return !!data && typeof data === "object" && !Array.isArray(data) && "version" in data
}

// Pure, state-free helpers for the quotation viewer orchestrator (useQuotationViewer). Kept here
// so the hook body stays focused on wiring; each takes data in and returns data out.

// Modal copy for the connect/move dialog (Figma "Connect object to elevation" / "Move to another
// elevation"). The confirm button is "Connect" in both. For "move" the helper names the current
// elevation when the whole selection shares one, otherwise falls back to a generic phrasing.
export const buildConnectModalCopy = (
	mode: "connect" | "move",
	furniture: TFurniture[],
	erefs: TEref[],
): { title: string; description: string } => {
	if (mode === "connect") {
		return {
			title: "Connect object to elevation",
			description: "Select an elevation on this page to link this object to.",
		}
	}
	const plural = furniture.length > 1
	const subject = plural ? "These objects are" : "This object is"
	const them = plural ? "them" : "it"
	const connectedErefIds = [...new Set(furniture.map((f) => f.eref_id).filter((id): id is number => id != null))]
	let where = "an elevation"
	if (connectedErefIds.length === 1) {
		const num = erefs.find((e) => e.id === connectedErefIds[0])?.elevation_number
		where = num ? `elevation ${num}` : "an elevation"
	} else if (connectedErefIds.length > 1) {
		where = "different elevations"
	}
	return {
		title: "Move to another elevation",
		description: `${subject} currently connected to ${where}. Select another elevation to reconnect ${them}.`,
	}
}

/**
 * What the shared delete-confirmation modal is about to delete: furniture (single or batch,
 * REQ-369) or an elevation reference (REQ-376). Lives here next to the copy builder because the
 * toolbar, the context menu and the Delete hotkey all raise the same modal.
 */
export type TDeleteConfirmTarget =
	| { kind: "furniture"; furniture: TFurniture[] }
	| { kind: "eref"; eref: TErefWithFurniture }
	| { kind: "ecall"; ecall: TEcall }

// Title + description for the shared delete-confirmation modal, quoted from the Figma frames.
// Furniture keeps the terse REQ-369 wording. An eref (REQ-295) and an ecall (REQ-297) both spell out
// that the delete is not a cascade — the backend detaches what was connected rather than deleting
// it — and the eref names itself, since a drawing carries many.
export const buildDeleteConfirmText = (target: TDeleteConfirmTarget): { title: string; description: string } => {
	if (target.kind === "ecall") {
		return {
			title: "Delete elevation callout?",
			description:
				"The elevation callout will be deleted, and all elevation references linked to it will be detached.",
		}
	}
	if (target.kind === "eref") {
		// REQ-295's Figma frame names the elevation the modal is about, the way the delete-object copy
		// names its object — with several elevations on a drawing, "this elevation reference" doesn't
		// say which one is about to go. An eref carrying neither an elevation number nor a page name
		// has nothing to name, so it keeps generic wording.
		const label = buildErefDisplayLabel(target.eref, "")
		if (!label) {
			return {
				title: "Delete elevation reference",
				description:
					"This elevation reference will be deleted. All objects currently linked to it will be detached.",
			}
		}
		return {
			title: `Delete elevation ${label}?`,
			description: `Elevation reference ${label} will be deleted. All objects currently linked to it will be detached.`,
		}
	}
	const plural = target.furniture.length > 1
	if (plural) {
		return {
			title: "Delete objects",
			description: `Delete ${target.furniture.length} selected objects? This can't be undone.`,
		}
	}
	// Name the object rather than calling it "this object": with several near-identical highlights on
	// a drawing, "Delete object" alone doesn't tell the user WHICH one the confirmation is about.
	// An unnameable object (no model number, no category at any level) keeps the generic wording.
	const label = getFurnitureActionLabel(target.furniture[0])
	if (!label) return { title: "Delete object", description: "Delete this object? This can't be undone." }
	return {
		title: `Delete object ${label}?`,
		description: `Object ${label} will be deleted. This can't be undone.`,
	}
}

// Whether a furniture has all the fields required to enter a quotation. Mirrors the right
// sidebar's `useFurnitureForm.isValid` from the furniture record alone (the sidebar additionally
// blocks on a missing price for the eref's series, which needs model-number pricing we don't
// fetch here — the backend still enforces that, so a rejected add surfaces as an error toast).
export const isFurnitureQuotationReady = (f: TFurniture): boolean => {
	if (f.eref_id == null) return false
	// #1346 — a custom object has no category either, so it MUST be settled before the Other test:
	// the inline "all three levels null" check this used to run claimed every custom object and sent
	// it down the fixed-price branch, which a custom object can never satisfy (the backend drops its
	// fixed-price config), leaving it permanently un-quotable. `isOtherFurniture` already excludes
	// custom; using it here is what keeps the two rules from drifting apart again.
	const isCustom = isCustomFurniture(f)
	const isOther = isOtherFurniture(f)
	const variant: "countertop" | "custom" | "other" | "typical" = isCustom
		? "custom"
		: isCountertop(f)
			? "countertop"
			: isOther
				? "other"
				: "typical"
	// selectedTopLevelCategory is null only when it isn't "Other"/"Custom" and no top-level is set.
	if (!isOther && !isCustom && f.top_level_category == null) return false
	if (variant === "other") {
		return f.fixed_price_config?.fixed_price != null && (f.fixed_price_config?.name ?? "").trim() !== ""
	}
	if (f.width == null || f.height == null || f.depth == null) return false
	// Its price comes from the linked definition, so the definition is the whole remaining
	// requirement — there are no category levels for it to fill in.
	if (variant === "custom") return f.custom_object_config?.custom_model_number != null
	if (f.super_category == null || f.category?.id == null) return false
	if (variant === "countertop") return true
	return f.selected_model_number != null
}

// Toast wording for an eref status change, derived from the transition (not just the target) so
// "reviewed" reads as "removed from quotation" when it comes back from `in_quotation`. Mirrors the
// furniture toasts, with the eref noun.
export const buildErefStatusToast = (current: TErefStatus, next: TErefStatus): string => {
	if (next === "in_quotation") return "Elevation reference added to quotation"
	if (next === "work_in_progress") return "Elevation reference marked as need review"
	return current === "in_quotation"
		? "Elevation reference removed from quotation"
		: "Elevation reference marked as reviewed"
}

// How an elevation reference is named in the viewer's bars: "<elevation number>-<page name>"
// (e.g. "17-A-101"). Either part can be missing on a freshly detected eref — `page_name` is typed
// non-null but the API does send null (see ConnectToElevationModal's own fallback) — so the
// separator only appears between parts that exist; with neither, the caller still gets wording that
// reads as a sentence.
export const buildErefDisplayLabel = (
	eref: { elevation_number?: string | null; page_name?: string | null },
	// The delete modal (REQ-295) passes "" so it can detect an unnameable eref and switch to
	// generic wording, rather than rendering "Delete elevation this elevation?".
	fallback = "this elevation",
): string => {
	const parts = [eref.elevation_number, eref.page_name].map((part) => (part ?? "").trim()).filter(Boolean)
	return parts.length > 0 ? parts.join("-") : fallback
}

/**
 * What the viewer's right panel is currently showing. Shared by the orchestrator, the jump
 * navigation and the hotkey dispatcher so the three can't drift apart as modes are added.
 * The create modes have no selected entity behind them. They are the forms opened after drawing a
 * new elevation reference (REQ-292), elevation callout (REQ-294), cabinet (REQ-293), countertop
 * (REQ-298), other object (REQ-299) or custom object (#1371) area.
 * `furniture-batch` is the multi-selection editor (REQ-403): the same surface as `furniture`, but
 * its subject is every object currently selected rather than one.
 */
export type TRightPanelMode =
	| "eref"
	| "furniture"
	| "furniture-batch"
	| "ecall"
	| "create-eref"
	| "create-ecall"
	| "create-countertop"
	| "create-furniture"
	| "create-other"
	| "create-custom"
	| null

/**
 * Name of a page, for the read-only `Page` field of the create-eref panel (REQ-292). `page_infos[]`
 * is the source of truth (it covers pages that have no detections at all); the erefs already on the
 * page are the fallback for a quotation whose page_infos hasn't caught up. Returns null when the
 * page has no name — which the endpoint can't accept, so the panel blocks Create instead.
 */
export const findPageName = (
	pageInfos: { page_number: number; page_name: string | null }[] | undefined,
	pages: { page_number: number; erefs: { page_name: string }[] }[] | undefined,
	pageNumber: number,
): string | null => {
	const fromInfos = pageInfos?.find((info) => info.page_number === pageNumber)?.page_name
	if (fromInfos) return fromInfos
	const fromErefs = pages?.find((page) => page.page_number === pageNumber)?.erefs.find((eref) => eref.page_name)
	return fromErefs?.page_name ?? null
}

/**
 * Elevation number to pre-fill the create-eref panel with: the LOWEST positive integer not already
 * used on that page, since the backend rejects a duplicate `(page_number, elevation_number)` pair.
 * Gaps are filled first — a page holding 1 and 3 suggests 2, not 4 — so deleting an elevation frees
 * its number for reuse. Purely numeric elevation numbers are the norm in the drawings we handle;
 * anything else on the page (e.g. "A12") is ignored rather than guessed at, and a page with no
 * numeric elevations starts at "1".
 */
export const suggestNextElevationNumber = (
	pages: { page_number: number; erefs: { elevation_number: string | null }[] }[] | undefined,
	pageNumber: number,
): string => {
	const erefs = pages?.find((page) => page.page_number === pageNumber)?.erefs ?? []
	const used = new Set<number>()
	for (const eref of erefs) {
		const raw = (eref.elevation_number ?? "").trim()
		// Guard the empty string explicitly: `Number("")` is 0, not NaN, so a blank elevation number
		// would otherwise register as a used value.
		if (!raw) continue
		const parsed = Number(raw)
		if (Number.isInteger(parsed) && parsed >= 1) used.add(parsed)
	}
	let candidate = 1
	while (used.has(candidate)) candidate += 1
	return String(candidate)
}

export const getEcallsForErefId = (ecalls: TEcall[], erefId: number | null | undefined): TEcall[] => {
	if (erefId == null) return []
	return ecalls.filter((e) => e.elevation_numbers.some((en) => en.eref_id === erefId))
}
